import React from 'react';
import { View, Text, type ViewProps } from 'react-native';
import { cn } from '@/utils/cn';

export type BadgeVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'outline';

export interface BadgeProps extends ViewProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  /** Exibe um ponto de status antes do texto */
  dot?: boolean;
  className?: string;
}

const badgeVariants: Record<BadgeVariant, string> = {
  default: 'bg-neutral-100',
  primary: 'bg-primary-50',
  secondary: 'bg-secondary-50',
  success: 'bg-success-50',
  warning: 'bg-warning-50',
  error: 'bg-error-50',
  info: 'bg-info-50',
  outline: 'bg-transparent border border-neutral-200',
};

const badgeTextVariants: Record<BadgeVariant, string> = {
  default: 'text-neutral-700',
  primary: 'text-primary-700',
  secondary: 'text-secondary-700',
  success: 'text-success-600',
  warning: 'text-warning-600',
  error: 'text-error-600',
  info: 'text-info-600',
  outline: 'text-neutral-600',
};

const badgeDotVariants: Record<BadgeVariant, string> = {
  default: 'bg-neutral-500',
  primary: 'bg-primary-500',
  secondary: 'bg-secondary-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  error: 'bg-error-500',
  info: 'bg-info-500',
  outline: 'bg-neutral-400',
};

export function Badge({ children, variant = 'default', dot, className, ...props }: BadgeProps) {
  return (
    <View
      className={cn(
        'inline-flex flex-row items-center gap-1.5 rounded-full px-2.5 py-1',
        badgeVariants[variant],
        className,
      )}
      {...props}
    >
      {dot && <View className={cn('h-1.5 w-1.5 rounded-full', badgeDotVariants[variant])} />}
      {typeof children === 'string' ? (
        <Text className={cn('font-semibold text-xs', badgeTextVariants[variant])}>{children}</Text>
      ) : (
        children
      )}
    </View>
  );
}
